import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { Card, SkeletonLoader } from '@payaca/ambrosia';
import { DealsPermissions } from '@payaca/permissions/deals/deals.permissions';

import { pick } from 'lodash-es';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useGetProjectByIdOverview from '../../../../api/queries/project/useGetProjectByIdOverview';
import useGetProjectProfitBreakdown from '../../../../api/queries/project/useGetProjectProfitBreakdown';
import ProfitBreakdown, {
  Props as ProfitBreakdownProps,
} from './ProjectHeader/ProfitBreakdown';

const ProjectOverviewCards: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { isLoading: isProjectLoading } = useGetProjectByIdOverview(projectId);
  const { data: projectProfitBreakdownData } =
    useGetProjectProfitBreakdown(projectId);

  const profitData = useMemo(() => {
    const profitBreakdown =
      projectProfitBreakdownData?.projectById.profitBreakdown;

    if (!profitBreakdown) {
      return;
    }

    const profitData: ProfitBreakdownProps['profitData'] = {
      revenue: pick(profitBreakdown.revenue, [
        'actual',
        'forecast',
        'committed',
      ]),
      materials: pick(profitBreakdown.materials, [
        'actual',
        'forecast',
        'committed',
      ]),
      labour: pick(profitBreakdown.labour, ['actual', 'forecast', 'committed']),
      profit: pick(profitBreakdown.profit, ['actual', 'forecast']),
    };
    return profitData;
  }, [projectProfitBreakdownData]);

  if (isProjectLoading) {
    return (
      <div className="flex flex-col gap-4 sm:flex-row">
        <Card className="flex-1" sizeVariant="lg">
          <Card.Body className="space-y-2">
            <SkeletonLoader.Text className="mb-4 w-1/4" />
            <SkeletonLoader.Text />
            <SkeletonLoader.Text />
            <SkeletonLoader.Text />
          </Card.Body>
        </Card>

        <Card className="flex-1" sizeVariant="lg">
          <Card.Body className="space-y-2">
            <SkeletonLoader.Text className="mb-4 w-1/4" />
            <SkeletonLoader.Text />
            <SkeletonLoader.Text />
            <SkeletonLoader.Text />
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-4 sm:flex-row-reverse">
        <PermissionGuard
          renderIfHasPermissions={[DealsPermissions.GET_PROFITABILITY]}
        >
          <ProfitBreakdown
            className="hidden sm:block sm:flex-auto"
            profitData={profitData}
          />
        </PermissionGuard>
      </div>
    </>
  );
};

export default ProjectOverviewCards;
